import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { io } from "socket.io-client";

import styles from "../../styles/__chatModal.module.scss";
import { IoAttachOutline, IoSend } from "react-icons/io5";
import { FaXmark } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import DefaultAvatar from "../../assets/images/default_avatar.png";
import AttachmentPreview from "./AttachmentPreview";
import StagedAttachment from "./StagedAttachment";
import ChatFileCard, { FILE_BUBBLE_STYLE } from "./ChatFileCard";
import axios from "axios";
import { ensureFreshAccessToken, isAuthError } from "../../utils/socketAuth";
import { APP_PATH } from "../../api/backendPath";
import { formatCustom as tzCustom } from "../../utils/datetime";
import { OPEN_CHAT_MESSAGE_EVENT, registerActiveChat, sameId } from "../../utils/activeChatRegistry";

/**
 * Real-time provider <-> patient chat (Socket.IO), backed by the shared notification-service
 * room chat. Mirrors the doctor app's AppointmentChat (token refresh, reliable emit,
 * file sharing, per-sender avatars, click-to-open attachments).
 *
 * Props:
 *  - patientUserId (number, required): the patient's USER id (booking.patient_id).
 *  - bookingId (number, optional): scopes the room to a specific booking/appointment.
 *  - title (string): header label (patient name).
 *  - providerAvatar (string, optional): the logged-in provider's avatar presigned URL.
 *  - isOpen (bool): whether the panel is visible (default true).
 *  - onClose (fn): close handler.
 *  - onUnreadChange (fn): reports unread count (for a badge on the chat button).
 *  - chatSource ("page" | "dock"): who renders this chat; used by the open-chat registry
 *    so the ChatDock can tell its own bubbles apart from page-level chat windows.
 */
const Chat = ({ onClose, patientUserId, bookingId, title = "Patient", subtitle, providerAvatar, isOpen = true, onUnreadChange, chatSource = "page" }) => {
  const { t } = useTranslation();
  // Sender name on the center's own messages: the center name the dashboard header caches.
  const selfName = sessionStorage.getItem("physio_name") || "";
  // Auth header read fresh from sessionStorage (works regardless of the app's
  // useAxiosPrivate hook being sync or async).
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${sessionStorage.getItem("usr_1a2b3c")}` }), []);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [roomId, setRoomId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fileUploading, setFileUploading] = useState(false);
  const [stagedFile, setStagedFile] = useState(null);
  const [failedImages, setFailedImages] = useState({});
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const roomIdRef = useRef(null);
  const isOpenRef = useRef(isOpen);
  const unreadRef = useRef(0);
  const refreshTimerRef = useRef(null);
  const initialScrolledRef = useRef(false);
  const stickToBottomRef = useRef(true);
  // Keep the latest onUnreadChange in a ref so reportUnread/markRoomRead stay stable
  // (the parent ChatDock passes a fresh callback on every render). Without this, the
  // "on open" effect re-ran on every re-render and kept yanking the view to the bottom.
  const onUnreadChangeRef = useRef(onUnreadChange);
  onUnreadChangeRef.current = onUnreadChange;
  // Ids of messages already shown — the same incoming message can arrive through this
  // chat's socket AND the global socket relay (see OPEN_CHAT_MESSAGE_EVENT).
  const seenMessageIdsRef = useRef(new Set());
  // Handle of this chat's entry in the open-chat registry (set while visible).
  const activeChatHandleRef = useRef(null);

  // Provider's own avatar: prop -> cached profile avatar (set by the dashboard layout)
  // -> default. Shows the provider's image when available, else the default avatar.
  const providerAvatarSrc = providerAvatar || sessionStorage.getItem("provider_avatar_url") || DefaultAvatar;

  // Current (provider) user id decoded from the JWT.
  const myUserId = useMemo(() => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.userId || payload.user_id || payload.sub || payload.id || null;
      }
    } catch (e) {}
    return null;
  }, []);

  // Attachment shown in the in-page preview (AttachmentPreview) instead of a new tab.
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const openAttachment = useCallback((url, message) => {
    if (!url) return;
    setPreviewAttachment({
      url,
      kind: message?.message_type,
      fileName: message?.metadata?.file_name || message?.message || "",
      contentType: message?.metadata?.content_type || "",
      fileSize: message?.metadata?.file_size,
    });
  }, []);

  const reportUnread = useCallback((count) => {
    const safe = Math.max(0, count || 0);
    unreadRef.current = safe;
    onUnreadChangeRef.current?.(safe);
  }, []);

  const markRoomRead = useCallback((rid) => {
    if (!rid) return;
    axios
      .post(`${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatMarkRead}`, { room_id: rid }, { headers: authHeaders() })
      // Once the server has the room as read, let the chat-icon badges re-sync (the
      // global handler skips its own refresh for messages shown in an open chat).
      .then(() => window.dispatchEvent(new CustomEvent("chat-unread-refresh", { detail: { room_id: rid } })))
      .catch(() => {});
    reportUnread(0);
  }, [authHeaders, reportUnread]);

  // Replace the list with server history, keeping live messages that arrived for this
  // room while the history request was in flight (otherwise they'd vanish until reopen).
  const applyHistory = useCallback((serverMessages) => {
    const list = Array.isArray(serverMessages) ? serverMessages : [];
    list.forEach((m) => seenMessageIdsRef.current.add(String(m.id)));
    setMessages((prev) => {
      const serverIds = new Set(list.map((m) => String(m.id)));
      const extras = prev.filter((m) => m._live && !serverIds.has(String(m.id)) && sameId(m.room_id, roomIdRef.current));
      return extras.length ? [...list, ...extras] : list;
    });
  }, []);

  // Handle a patient message for this conversation. Called by this chat's own socket and
  // by the global-socket relay (useSocketNotifications) while this chat is open.
  const handleIncomingMessage = (data) => {
    if (!data || data.message_id == null) return;
    const rid = roomIdRef.current;
    if (rid) {
      // Only messages for THIS conversation's room (ids may be numbers or strings).
      if (!sameId(data.room_id, rid)) return;
    } else if (
      // Room not resolved yet: every chat message for the provider arrives on the user
      // channel, so accept only this booking's patient.
      !sameId(data.sender_id, patientUserId) ||
      (bookingId != null && !sameId(data.appointment_id, bookingId))
    ) {
      return;
    }
    if (sameId(data.sender_id, myUserId)) return;

    const key = String(data.message_id);
    if (seenMessageIdsRef.current.has(key)) return;
    seenMessageIdsRef.current.add(key);

    setMessages((prev) => {
      if (prev.some((m) => sameId(m.id, data.message_id))) return prev;
      return [
        ...prev,
        {
          id: data.message_id,
          room_id: data.room_id,
          sender_id: data.sender_id,
          message: data.message,
          message_type: data.message_type || "text",
          metadata: data.metadata || null,
          created_at: data.timestamp || new Date().toISOString(),
          _live: true,
        },
      ];
    });
    if (isOpenRef.current) {
      // Chat is open and visible -> the message is read on arrival.
      markRoomRead(roomIdRef.current || data.room_id);
    } else {
      // Chat hidden (minimized dock) -> bump the unread badge.
      reportUnread(unreadRef.current + 1);
    }
  };
  // Socket listeners are registered once per connection; always call the latest handler.
  const handleIncomingRef = useRef(handleIncomingMessage);
  handleIncomingRef.current = handleIncomingMessage;

  // Reliable send: wait for the socket to reconnect instead of dropping the message,
  // and retry if the server doesn't ack success.
  const emitMessageReliably = useCallback(function emit(payload, attempt = 0) {
    const socket = socketRef.current;
    if (!socket) return;
    if (socket.connected) {
      socket.emit("send_message", payload, (ack) => {
        if (!ack?.success && attempt < 4) setTimeout(() => emit(payload, attempt + 1), 1000);
      });
    } else if (attempt < 20) {
      setTimeout(() => emit(payload, attempt + 1), 500);
    }
  }, []);

  useEffect(() => {
    // Switching conversation within the same instance: start from a clean slate so the
    // previous conversation's live messages are not merged into this one.
    seenMessageIdsRef.current = new Set();
    setMessages((prev) => (prev.length ? [] : prev));
    const token = sessionStorage.getItem("usr_1a2b3c");
    if (!token || !patientUserId) {
      setLoading(false);
      return;
    }

    const socketUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
    const socket = io(socketUrl, {
      query: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      // Keep retrying: a chat that gave up stops receiving messages until it is reopened.
      reconnectionAttempts: Infinity,
    });
    socketRef.current = socket;

    // Proactively refresh the access token and reconnect with a fresh handshake
    // before it expires (the auth guard reads the token from the handshake query).
    const scheduleTokenRefresh = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      const tkn = sessionStorage.getItem("usr_1a2b3c");
      if (!tkn) return;
      let expMs = 0;
      try {
        const payload = JSON.parse(atob(tkn.split(".")[1]));
        if (payload.exp) expMs = payload.exp * 1000;
      } catch (e) {}
      if (!expMs) return;
      const delay = Math.max(5000, expMs - Date.now() - 30000);
      refreshTimerRef.current = setTimeout(async () => {
        try {
          // Reads the tokens at call time (a refresh token captured at mount is stale after
          // the first rotation) and shares one refresh with every other socket.
          const accessToken = await ensureFreshAccessToken(socket.io.opts.query?.token);
          if (accessToken) {
            socket.io.opts.query = { token: accessToken };
            socket.disconnect().connect();
          }
        } catch (e) {}
      }, delay);
    };

    socket.on("connect", () => {
      scheduleTokenRefresh();
      socket.emit(
        "get_or_create_room",
        // Per-appointment conversation: the SAME booking reuses the SAME chat across
        // every screen (order management, dashboard, appointment detail). Different
        // bookings get different chats.
        { other_user_id: Number(patientUserId), appointment_id: bookingId ? Number(bookingId) : undefined },
        (ack) => {
          if (ack?.success && ack?.room_id) {
            const rid = ack.room_id;
            roomIdRef.current = rid;
            setRoomId(rid);
            // Publish the resolved room in the open-chat registry so the global
            // notification handler / ChatDock recognise this conversation as open.
            activeChatHandleRef.current?.update({ roomId: rid });
            socket.emit("join_room", { room_id: rid });
            socket.emit("get_messages", { room_id: rid, page: 1, limit: 100 }, (msgAck) => {
              if (msgAck?.success && Array.isArray(msgAck.messages)) applyHistory(msgAck.messages);
              setLoading(false);
            });
            if (isOpenRef.current) {
              markRoomRead(rid);
            } else {
              axios
                .get(`${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatUnreadCount}`, { params: { room_id: rid }, headers: authHeaders() })
                .then((res) => reportUnread(res?.data?.data?.unread_count ?? res?.data?.data?.count ?? 0))
                .catch(() => {});
            }
          } else {
            setLoading(false);
          }
        }
      );
    });

    socket.on("connect_error", async (err) => {
      // socket.io only retries transport failures by itself (`active`); a refused handshake
      // needs a fresh token and a manual connect().
      if (socket.active) return;
      try {
        const accessToken = await ensureFreshAccessToken(isAuthError(err) ? socket.io.opts.query?.token : null);
        if (accessToken && socketRef.current === socket) {
          socket.io.opts.query = { token: accessToken };
          setTimeout(() => {
            if (socketRef.current === socket && !socket.connected) socket.connect();
          }, 1000);
        }
      } catch (e) {}
    });

    socket.on("disconnect", (reason) => {
      // Closed by the server: socket.io does not reconnect by itself.
      if (reason === "io server disconnect") {
        setTimeout(() => {
          if (socketRef.current === socket && !socket.connected) {
            const latest = sessionStorage.getItem("usr_1a2b3c");
            if (latest) socket.io.opts.query = { token: latest };
            socket.connect();
          }
        }, 2000);
      }
    });

    socket.io.on("reconnect_attempt", () => {
      const tkn = sessionStorage.getItem("usr_1a2b3c");
      if (tkn) socket.io.opts.query = { token: tkn };
    });

    socket.on("new_message", (data) => {
      handleIncomingRef.current(data);
    });

    return () => {
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
      try { if (roomIdRef.current) socket.emit("leave_room", { room_id: roomIdRef.current }); } catch (e) {}
      socket.disconnect();
      socketRef.current = null;
      roomIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientUserId, bookingId, myUserId]);

  // React to open/close: clear unread + mark read + refresh history when opened.
  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen && roomIdRef.current) {
      initialScrolledRef.current = false; // re-pin to bottom when reopened
      stickToBottomRef.current = true;
      markRoomRead(roomIdRef.current);
      if (socketRef.current?.connected) {
        socketRef.current.emit("get_messages", { room_id: roomIdRef.current, page: 1, limit: 100 }, (msgAck) => {
          if (msgAck?.success && Array.isArray(msgAck.messages)) applyHistory(msgAck.messages);
        });
      }
    }
  }, [isOpen, roomId, markRoomRead, applyHistory]);

  // Messages relayed from the global notification socket while this chat is open
  // (safety net if this chat's own socket is momentarily reconnecting).
  useEffect(() => {
    const onRelay = (e) => {
      if (!isOpenRef.current) return;
      handleIncomingRef.current(e.detail);
    };
    window.addEventListener(OPEN_CHAT_MESSAGE_EVENT, onRelay);
    return () => window.removeEventListener(OPEN_CHAT_MESSAGE_EVENT, onRelay);
  }, []);

  // Register in the open-chat registry while visible, so the global new_message handler
  // and ChatDock don't toast / pop a dock bubble for this conversation. A Map-based
  // registry (not a single global room id) so several open chats can't clear each other.
  useEffect(() => {
    if (!isOpen || !patientUserId) return undefined;
    const handle = registerActiveChat({
      roomId: roomIdRef.current,
      appointmentId: bookingId,
      patientUserId,
      source: chatSource,
    });
    activeChatHandleRef.current = handle;
    return () => {
      handle.unregister();
      if (activeChatHandleRef.current === handle) activeChatHandleRef.current = null;
    };
  }, [isOpen, patientUserId, bookingId, chatSource]);

  useEffect(() => {
    if (roomId) activeChatHandleRef.current?.update({ roomId });
  }, [roomId]);

  // Smoothly keep the view pinned to the newest message, but only while the user is
  // at (or near) the bottom. The moment they scroll up to read history we stop
  // auto-scrolling so the view stays where they put it.
  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // Defer to next paint so the new content height is measured correctly.
    requestAnimationFrame(() => {
      const node = messagesContainerRef.current;
      if (node) node.scrollTop = node.scrollHeight;
    });
  }, []);

  // Track whether the user is parked at the bottom. Driven by their manual scrolling.
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }, []);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || messages.length === 0) return;
    // Always pin to the bottom on the first batch of messages (initial load / reopen).
    if (!initialScrolledRef.current) {
      stickToBottomRef.current = true;
      scrollToBottom();
      initialScrolledRef.current = true;
      return;
    }
    // Otherwise only follow new messages when the user hasn't scrolled up.
    if (stickToBottomRef.current) scrollToBottom();
  }, [messages, scrollToBottom]);

  // Refresh history when the tab/window regains focus, so messages/files sent from
  // another screen (e.g. Order Management vs Dashboard) by the same user show up here.
  useEffect(() => {
    const reload = () => {
      if (document.visibilityState === "hidden") return;
      const rid = roomIdRef.current;
      if (!rid || !socketRef.current?.connected) return;
      socketRef.current.emit("get_messages", { room_id: rid, page: 1, limit: 100 }, (msgAck) => {
        if (msgAck?.success && Array.isArray(msgAck.messages)) applyHistory(msgAck.messages);
      });
    };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", reload);
    return () => {
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", reload);
    };
  }, [applyHistory]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    // Local copy for the "ready to send" card's Preview (PDFs too) and the bubble while uploading.
    const previewUrl = URL.createObjectURL(file);
    // Picking another file replaces the staged one: release its local copy.
    if (stagedFile?.previewUrl) URL.revokeObjectURL(stagedFile.previewUrl);
    setStagedFile({ file, isImage, previewUrl });
    e.target.value = "";
  };

  const handleSendMessage = async () => {
    const hasText = newMessage.trim().length > 0;
    const hasFile = !!stagedFile;
    if ((!hasText && !hasFile) || !roomId) return;

    // Sending a message means we want to follow the conversation to the bottom.
    stickToBottomRef.current = true;

    if (hasFile) {
      setFileUploading(true);
      const file = stagedFile.file;
      const isImage = stagedFile.isImage;
      const tempId = `local_${Date.now()}`;
      const caption = hasText ? newMessage.trim() : file.name;
      setMessages((prev) => [...prev, { id: tempId, sender_id: myUserId, message: file.name, message_type: isImage ? "image" : "file", metadata: { file_name: file.name, previewUrl: stagedFile.previewUrl }, created_at: new Date().toISOString() }]);
      setStagedFile(null);
      setNewMessage("");
      try {
        const formData = new FormData();
        formData.append("file", file);
        const uploadResp = await axios.post(`${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatUpload}`, formData, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
        const s3Key = uploadResp?.data?.data?.s3_key;
        const presignedUrl = uploadResp?.data?.data?.presigned_url;
        if (!s3Key) throw new Error("Upload failed");
        const metadata = { s3_key: s3Key, file_name: file.name, file_size: file.size, content_type: file.type, presigned_url: presignedUrl };
        setMessages((prev) => prev.map((m) => (m.id === tempId ? { ...m, message: caption, metadata: { ...m.metadata, ...metadata } } : m)));
        emitMessageReliably({ room_id: roomId, message: caption, message_type: isImage ? "image" : "file", metadata });
      } catch (err) {
        console.error("File upload failed:", err);
      } finally {
        setFileUploading(false);
      }
      return;
    }

    const text = newMessage.trim();
    setNewMessage("");
    setMessages((prev) => [...prev, { id: `local_${Date.now()}`, room_id: roomId, sender_id: myUserId, message: text, message_type: "text", created_at: new Date().toISOString() }]);
    emitMessageReliably({ room_id: roomId, message: text, message_type: "text" });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const formatTime = (iso) => {
    try {
      return tzCustom(new Date(iso), { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.chatMain}>
      {previewAttachment && (
        <AttachmentPreview
          url={previewAttachment.url}
          kind={previewAttachment.kind}
          fileName={previewAttachment.fileName}
          contentType={previewAttachment.contentType}
          fileSize={previewAttachment.fileSize}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
      <div className={styles.innerWrapper}>
        <div className={`${styles["settings-card"]}`}>
          <div className={styles.chatContainer}>
            {/* Chat Header */}
            <div className={styles.chatHeader}>
              <div className={styles.chatHeaderLeft}>
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25, minWidth: 0 }}>
                  <h6 className={styles.chatTitle} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</h6>
                  {subtitle ? (
                    <span style={{ fontSize: 12, color: "#0a7d7d", fontWeight: 600 }}>{subtitle}</span>
                  ) : null}
                </div>
              </div>
              <button className={styles.iconButton} onClick={onClose}>
                <FaXmark size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className={styles.chatMessages} ref={messagesContainerRef} onScroll={handleMessagesScroll}>
              {loading ? (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "1.5rem", fontSize: "0.9rem" }}>
                  {t("loading", "Loading…")}
                </div>
              ) : !patientUserId ? (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "1.5rem", fontSize: "0.9rem" }}>
                  {t("chat.noPatient", "No patient selected for this conversation.")}
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "1.5rem", fontSize: "0.9rem" }}>
                  {t("chat.empty", "No messages yet. Start the conversation.")}
                </div>
              ) : (
                messages.map((message) => {
                  const isMine = Number(message.sender_id) === Number(myUserId);
                  const isOther = !isMine;
                  const isFile = message.message_type === "image" || message.message_type === "file";
                  const fileUrl = message.metadata?.presigned_url || message.metadata?.previewUrl;
                  return (
                    <div
                      key={message.id}
                      className={`${styles.messageWrapper} ${isOther ? styles.adminTxt : styles.userTxt}`}
                    >
                      <div className="">
                        <div className={styles.messageTime}>
                          {/* Sender name on every message (patient and center). */}
                          {(isOther ? title : selfName) && (
                            <>
                              <span style={{ fontWeight: 600, color: "#374151" }}>{isOther ? title : selfName}</span>
                              &nbsp;|&nbsp;
                            </>
                          )}
                          {formatTime(message.created_at)}{" "}
                        </div>
                        <div
                          className={`${styles.messageBubble} ${isOther ? styles.admin : styles.user}`}
                          style={isFile && !(message.message_type === "image" && fileUrl && !failedImages[message.id]) ? FILE_BUBBLE_STYLE : undefined}
                        >
                          {isFile && message.message_type === "image" && fileUrl && !failedImages[message.id] ? (
                            <img
                              src={fileUrl}
                              alt="attachment"
                              style={{ maxWidth: "180px", maxHeight: "180px", borderRadius: "8px", cursor: "pointer", display: "block" }}
                              onClick={() => openAttachment(fileUrl, message)}
                              onError={() => setFailedImages((prev) => ({ ...prev, [message.id]: true }))}
                            />
                          ) : isFile ? (
                            <ChatFileCard
                              fileName={message.metadata?.file_name || message.message}
                              contentType={message.metadata?.content_type}
                              fileSize={message.metadata?.file_size}
                              mine={isMine}
                              disabled={!fileUrl}
                              onOpen={() => openAttachment(fileUrl, message)}
                            />
                          ) : (
                            <p className={styles.messageText}>{message.message}</p>
                          )}
                        </div>
                      </div>
                      {isMine && (
                        <div className={styles.userImgWrap}>
                          <img
                            src={providerAvatarSrc}
                            alt="Me"
                            className={styles.userImg}
                            style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
                            onError={(e) => { e.currentTarget.src = DefaultAvatar; }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Staged file (picked, not sent yet) */}
            {stagedFile && (
              <StagedAttachment
                file={stagedFile.file}
                isImage={stagedFile.isImage}
                previewUrl={stagedFile.previewUrl}
                onPreview={setPreviewAttachment}
                onRemove={() => {
                  if (stagedFile.previewUrl) URL.revokeObjectURL(stagedFile.previewUrl);
                  setStagedFile(null);
                }}
              />
            )}

            {/* Message Input (the staged file card above carries the divider when shown) */}
            <div className={styles.chatFooter} style={stagedFile ? { borderTop: "none" } : undefined}>
              <div className={styles.inputWrapper}>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx"
                />
                <input
                  type="text"
                  className={styles.inputField}
                  placeholder={stagedFile ? t("chat.preview.captionPlaceholder") : t("chat.typeMessage", "Type a message")}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  disabled={!roomId || fileUploading}
                />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!roomId || fileUploading} aria-label={stagedFile ? t("chat.preview.chooseAnother") : t("chat.preview.attachFile")} title={stagedFile ? t("chat.preview.chooseAnother") : t("chat.preview.attachFile")}>
                  <IoAttachOutline size={24} />
                </button>
                <button
                  className={styles.sendButton}
                  onClick={handleSendMessage}
                  disabled={!roomId || (!newMessage.trim() && !stagedFile) || fileUploading}
                >
                  <IoSend size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
